import React from 'react';
import { EpiContext, EpiEditContext } from '../Store/EpiContext/Types';

export interface EpiContent {
    value: string;
    propertyDataType: string;
}

export interface EpiPageReference {
    value: any;
    propertyDataType: string;
}

export interface EpiLinkCollection {
    value: any[];
    propertyDataType: string;
}

const stringPropertyDataType = 'PropertyLongString';
const HtmlStringPropertyDataType = 'PropertyXhtmlString';

const resolve = <T extends {}>(
    context: EpiContext<T>,
    key: keyof T,
    zIndex = 1,
    variablesMap: Map<string, string> | null = null,
): JSX.Element | null => {
    const model = context.epiModel;
    const epiEditContext = context.epiEditContext;
    if (model != null && model[key] !== undefined) {
        const epiValue = (model[key] as any) as EpiContent;
        if (!harVerdi(epiValue) && !epiEditContext.isEditable) {
            return null;
        }

        if (epiValue.propertyDataType === stringPropertyDataType) {
            return spanValue<T>(key, epiValue, epiEditContext, zIndex, variablesMap);
        } else if (epiValue.propertyDataType === HtmlStringPropertyDataType) {
            return (
                <div
                    data-epi-edit={canOPE(epiEditContext, zIndex) ? key : null}
                    dangerouslySetInnerHTML={{ __html: replaceVariablesWithValues(epiValue.value, variablesMap) }}
                />
            );
        } else {
            return epiEditContext.isEditable ? (
                <span style={{ color: 'red' }}>
                    Unsupported datatype: {epiValue.propertyDataType} for: {key}
                </span>
            ) : null;
        }
    }
    return epiEditContext.isEditable ? <span style={{ color: 'red' }}>Could not find property: {key}</span> : null;
};

export const resolveRawText = <T extends {}>(
    context: EpiContext<T>,
    key: keyof T,
    variablesMap: Map<string, string> | null = null,
): string => {
    const model = context.epiModel;
    if (model != null && model[key] !== undefined) {
        const epiValue = (model[key] as any) as EpiContent;
        return replaceVariablesWithValues(epiValue.value, variablesMap);
    }
    return '';
};

export const resolvePageReferenceUrl = <T extends {}>(context: EpiContext<T>, key: keyof T): string => {
    const model = context.epiModel;
    if (model != null && model[key] !== undefined) {
        const epiPageReference = (model[key] as any) as EpiPageReference;
        if (epiPageReference.value != null) {
            return epiPageReference.value.url;
        }
    }
    return '';
};

export const resolveLinkCollection = <T extends {}>(
    context: EpiContext<T>,
    key: keyof T,
    wrapperClass?: string,
    aClass?: string,
): JSX.Element | null => {
    const model = context.epiModel;
    const epiEditContext = context.epiEditContext;
    if (model != null && model[key] !== undefined) {
        const epiLinkCollection = (model[key] as any) as EpiLinkCollection;
        const items: JSX.Element[] = [];
        for (let i = 0; i < epiLinkCollection.value.length; i++) {
            items.push(
                <a key={epiLinkCollection.value[i].text} className={aClass} href={epiLinkCollection.value[i].href}>
                    {epiLinkCollection.value[i].text}
                </a>,
            );
        }

        if (epiEditContext.isEditable) {
            return (
                <div data-epi-edit={key} className={wrapperClass}>
                    {items}
                </div>
            );
        } else {
            if (epiLinkCollection.value.length > 0) {
                return (
                    <div key={key.toString()} className={wrapperClass}>
                        {items}
                    </div>
                );
            }
        }
    }
    return null;
};

export default resolve;

const replaceVariablesWithValues = (epiValue: string, variablesMap: Map<string, string> | null): string => {
    let updatedString = epiValue;
    if (variablesMap !== null) {
        variablesMap.forEach((value: string, key: string): void => {
            const escapedKey = key.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
            updatedString = updatedString.replace(new RegExp(escapedKey, 'g'), value);
        });
    }
    return updatedString;
};

function harVerdi(epiValue: EpiContent): boolean {
    return epiValue.value != null && epiValue.value.length > 0;
}

// OPE: On page edit
function canOPE(epiEditContext: EpiEditContext, zIndex: number): boolean {
    return epiEditContext.isEditable && zIndex === epiEditContext.editZIndex;
}

function spanValue<T extends {}>(
    propertyName: keyof T,
    epiValue: EpiContent,
    epiEditContext: EpiEditContext,
    zIndex: number,
    variablesMap: Map<string, string> | null,
): JSX.Element {
    return (
        <span data-epi-edit={canOPE(epiEditContext, zIndex) ? propertyName : null}>
            {epiValue.value ? replaceVariablesWithValues(epiValue.value, variablesMap) : ''}
        </span>
    );
}
